import type { RetryPolicy } from '../types';
import { fetchWithTimeout } from './fetchWithTimeout';

interface RetryMetadata {
  attempt: number;
  reason: 'status' | 'error';
  status?: number;
  error?: Error;
}

interface RetryHooks {
  onRetry?: (metadata: RetryMetadata) => void;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Request aborted', 'AbortError');
}

/** Cancelling a send must also cancel its backoff, before another request can start. */
function waitForRetry(delayMs: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortReason(signal));
      return;
    }
    const cleanup = () => signal?.removeEventListener('abort', onAbort);
    const onAbort = () => {
      clearTimeout(timer);
      cleanup();
      reject(abortReason(signal!));
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, delayMs);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export async function fetchWithRetry(
  url: string,
  init: RequestInit & { timeoutMs?: number },
  retryPolicy?: RetryPolicy,
  hooks?: RetryHooks
): Promise<Response> {
  const retries = retryPolicy?.retries ?? 0;
  const backoffMs = retryPolicy?.backoffMs ?? 500;
  const retryStatuses = retryPolicy?.retryOnStatuses ?? [429, 500, 502, 503, 504];

  let attempt = 0;
  while (true) {
    if (init.signal?.aborted) throw abortReason(init.signal);
    try {
      const res = await fetchWithTimeout(url, init);
      if (!res.ok && attempt < retries && retryStatuses.includes(res.status)) {
        attempt += 1;
        hooks?.onRetry?.({ attempt, reason: 'status', status: res.status });
        // Release an unused error body without letting a broken reader delay the retry.
        try {
          void res.body?.cancel().catch(() => {});
        } catch {
          /* Already consumed or locked. */
        }
        await waitForRetry(Math.min(backoffMs * attempt, 10_000), init.signal);
        continue;
      }
      return res;
    } catch (error) {
      if (isAbortError(error) || init.signal?.aborted) {
        throw error;
      }
      if (attempt < retries) {
        attempt += 1;
        hooks?.onRetry?.({
          attempt,
          reason: 'error',
          error: error instanceof Error ? error : new Error(String(error)),
        });
        await waitForRetry(Math.min(backoffMs * attempt, 10_000), init.signal);
        continue;
      }
      throw error;
    }
  }
}
